import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  careerStats,
  deriveAchievements
} from '@h2h/shared';
import { useAuth } from '../../context/AuthContext';
import {
  useUser,
  useProfile,
  useMyMembership,
  useMyMemberships,
  useMyDuels,
  useMiniLeagues,
  usePlayerRound,
  useUsers,
  useLeagues,
  useMySeasonResults,
  useNotifications,
  useAddFriend,
  useRemoveFriend,
  useBlockedIds,
  useBlockUser,
  useUnblockUser,
  useReportUser
} from '../../hooks/useQueries';
import { useActiveLeague } from '../../hooks/useActiveLeague';
import type { ReportUserInput } from '../../lib/api';
import { identityFor } from '../../data/leagues';
import { toast } from '../../lib/toast';
import { statLine } from '../user-profile/helpers';

/**
 * Unified profile state for `/play/:leagueId/profile` (you) and `/u/:userId` (any player).
 * Every stat, pick, and standing is scoped to the selected league — switch worlds with
 * the league switcher in the hero.
 */
export function usePlayerProfileScreen() {
  const navigate = useNavigate();
  const { leagueId: routeLeagueId, userId: routeUserId } = useParams<{ leagueId?: string; userId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryLeagueId = searchParams.get('league');
  const queryRoundId = searchParams.get('round');
  const { uid, user } = useAuth();
  const { leagueId: activeLeagueId } = useActiveLeague();

  const isPublicRoute = !!routeUserId;
  const targetUserId = isPublicRoute ? routeUserId! : uid!;
  const isSelf = targetUserId === uid;

  const { data: targetUser, isLoading: userLoading } = useUser(targetUserId);
  const { data: me } = useProfile(uid);
  const { data: myMemberships } = useMyMemberships(uid);
  const { data: theirMemberships } = useMyMemberships(isSelf ? uid : targetUserId);
  const { data: leagues } = useLeagues();
  const { data: notifications } = useNotifications(isSelf ? uid : null);

  const sharedMemberships = useMemo(() => {
    if (isSelf) return myMemberships ?? [];
    const mine = new Set((myMemberships ?? []).map((m) => m.leagueId));
    return (theirMemberships ?? []).filter((m) => mine.has(m.leagueId));
  }, [isSelf, myMemberships, theirMemberships]);

  const leagueId = useMemo(() => {
    if (isPublicRoute) {
      const candidate = queryLeagueId ?? routeLeagueId ?? activeLeagueId;
      if (candidate && sharedMemberships.some((m) => m.leagueId === candidate)) return candidate;
      return sharedMemberships[0]?.leagueId ?? null;
    }
    return routeLeagueId ?? activeLeagueId ?? null;
  }, [isPublicRoute, queryLeagueId, routeLeagueId, activeLeagueId, sharedMemberships]);

  const setLeagueId = (id: string) => {
    if (isPublicRoute) {
      const next = new URLSearchParams(searchParams);
      next.set('league', id);
      next.delete('round');
      setSearchParams(next, { replace: true });
    } else {
      navigate(`/play/${id}/profile`);
    }
  };

  const { data: membership } = useMyMembership(leagueId ?? undefined, targetUserId);
  const { data: myMembershipInLeague } = useMyMembership(leagueId ?? undefined, uid);
  const { data: seasonResults } = useMySeasonResults(leagueId ?? undefined, targetUserId);

  const blockedIds = useBlockedIds(uid);
  const isBlocked = !isSelf && blockedIds.has(targetUserId);
  const isFollowing = !!(me?.friendIds ?? []).includes(targetUserId);

  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [selectedRoundId, setSelectedRoundId] = useState<string | null>(null);

  useEffect(() => {
    setSelectedRoundId(null);
  }, [leagueId, targetUserId]);

  useEffect(() => {
    if (!isPublicRoute || !leagueId) return;
    if (queryLeagueId === leagueId) return;
    const next = new URLSearchParams(searchParams);
    next.set('league', leagueId);
    if (queryLeagueId && queryLeagueId !== leagueId) next.delete('round');
    setSearchParams(next, { replace: true });
  }, [isPublicRoute, leagueId, queryLeagueId, searchParams, setSearchParams]);

  const showPicks = !!leagueId && !isBlocked && !!membership;
  const picksRoundId = leagueId && leagueId === queryLeagueId ? queryRoundId : null;

  const { data: picks, isLoading: picksLoading, isError: picksError } = usePlayerRound(
    showPicks ? leagueId! : undefined,
    showPicks ? targetUserId : null,
    selectedRoundId ?? picksRoundId
  );

  const addFriend = useAddFriend(uid);
  const removeFriend = useRemoveFriend(uid);
  const blockUser = useBlockUser(uid);
  const unblockUser = useUnblockUser(uid);
  const reportUser = useReportUser();

  const { data: myDuels } = useMyDuels(uid);
  const { data: myMinis } = useMiniLeagues(uid);

  const series = useMemo(
    () =>
      (myDuels ?? []).filter(
        (d) =>
          d.status === 'RESOLVED' &&
          d.participantIds.includes(targetUserId) &&
          (!leagueId || d.leagueId === leagueId)
      ),
    [myDuels, targetUserId, leagueId]
  );
  const myWins = series.filter((d) => d.winnerId === uid).length;
  const theirWins = series.filter((d) => d.winnerId === targetUserId).length;
  const draws = series.filter((d) => d.winnerId == null).length;
  const hasRivalry = series.length > 0;

  const sharedMinis = useMemo(
    () => (myMinis ?? []).filter((l) => l.memberIds.includes(targetUserId)),
    [myMinis, targetUserId]
  );

  const mutualIds = useMemo(() => {
    const mine = new Set(me?.friendIds ?? []);
    return (targetUser?.friendIds ?? []).filter((id) => mine.has(id) && id !== uid);
  }, [me?.friendIds, targetUser?.friendIds, uid, targetUser]);
  const { data: mutualUsers } = useUsers(mutualIds);

  const league = leagues?.find((l) => l.leagueId === leagueId);
  const leagueName = league?.name ?? identityFor(leagueId ?? '')?.name ?? 'This league';

  const stats = useMemo(() => careerStats(membership ? [membership] : []), [membership]);
  const statValues = useMemo(() => statLine(membership ? [membership] : []), [membership]);
  const myStatValues = useMemo(() => statLine(myMembershipInLeague ? [myMembershipInLeague] : []), [myMembershipInLeague]);

  const achievements = deriveAchievements(stats);
  const earnedCount = achievements.filter((a) => a.earned).length;

  const roundHistory = seasonResults ?? [];

  function onReport(reason: NonNullable<ReportUserInput['reason']>) {
    reportUser.mutate(
      { userId: targetUserId, reason },
      {
        onSuccess: () => {
          toast('Thanks — our team will review this.');
          setReportOpen(false);
        },
        onError: () => toast('Could not send the report. Try again.')
      }
    );
  }

  function onConfirmBlock() {
    blockUser.mutate(
      { userId: targetUserId },
      {
        onSuccess: () => {
          toast('Player blocked.');
          setBlockOpen(false);
        },
        onError: () => toast('Could not block this player. Try again.')
      }
    );
  }

  function onUnblock() {
    unblockUser.mutate(targetUserId, {
      onSuccess: () => toast('Player unblocked.'),
      onError: () => toast('Could not unblock. Try again.')
    });
  }

  const unread =
    isSelf && leagueId
      ? (notifications ?? []).filter((n) => n.readAt == null && n.data?.leagueId === leagueId).length
      : 0;

  const loading = userLoading || (isSelf ? membership === undefined : membership === undefined && !!leagueId);
  const hasSharedLeague = isSelf ? (myMemberships?.length ?? 0) > 0 : sharedMemberships.length > 0;
  const notInLeague = !!leagueId && membership === null;
  const noLeague = !leagueId && !loading;

  return {
    navigate,
    uid,
    user,
    targetUser,
    targetUserId,
    isSelf,
    isPublicRoute,
    isBlocked,
    isFollowing,
    leagueId,
    leagueName,
    setLeagueId,
    sharedMemberships,
    membership,
    stats,
    statValues,
    myStatValues,
    achievements,
    earnedCount,
    roundHistory,
    picks,
    picksLoading,
    picksError,
    showPicks,
    selectedRoundId,
    setSelectedRoundId,
    loading,
    userLoading,
    hasSharedLeague,
    notInLeague,
    noLeague,
    unread,
    menuOpen,
    setMenuOpen,
    reportOpen,
    setReportOpen,
    blockOpen,
    setBlockOpen,
    onReport,
    onConfirmBlock,
    onUnblock,
    addFriend,
    removeFriend,
    blockUser,
    unblockUser,
    reportUser,
    series,
    myWins,
    theirWins,
    draws,
    hasRivalry,
    sharedMinis,
    mutualIds,
    mutualUsers,
    me
  };
}

export type PlayerProfileVM = ReturnType<typeof usePlayerProfileScreen>;
